import { useState } from 'react';

import { useDuplicateJobMutation } from '@/features/jobs/api/jobsApi';
import {
  Badge,
  ConfirmActionModal,
  OptionsMenu,
  type BadgeTone,
} from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import { ArchiveJobConfirmModal } from './ArchiveJobConfirmModal';
import { CompleteJobModal } from './CompleteJobModal';
import styles from './JobCard.module.scss';

import type { Job, JobStatus } from '@/features/jobs/types';
import type { Dayjs } from 'dayjs';

type JobCardProps = {
  job: Job;
  onOpen: (jobId: string) => void;
  onOpenSettings: (jobId: string) => void;
  onTransition: (jobId: string, to: JobStatus) => Promise<void> | void;
  onComplete: (jobId: string, completionDate: string) => Promise<void> | void;
  onDelete: (jobId: string) => Promise<void> | void;
  onDuplicated?: () => Promise<void> | void;
  isTransitioning: boolean;
  isCompleting: boolean;
  isDeleting: boolean;
  canTransitionJobs: boolean;
  canDeleteJobs: boolean;
  canDuplicateJobs: boolean;
};

function formatJobCode(jobNumber: number | null | undefined) {
  if (!jobNumber) {
    return 'Job';
  }

  return `Job ${jobNumber}`;
}

function formatJobKindLabel(jobKind: string) {
  if (jobKind === 'retainer') {
    return 'Retainer';
  }

  return 'Project';
}

function formatJobStatusLabel(status: JobStatus) {
  if (status === 'active') {
    return 'Active';
  }
  if (status === 'pending') {
    return 'Pending';
  }
  if (status === 'completed') {
    return 'Completed';
  }
  if (status === 'archived' || status === 'archive') {
    return 'Archived';
  }

  return status.charAt(0).toUpperCase() + status.slice(1);
}

function getStatusBadgeTone(status: JobStatus): BadgeTone {
  if (status === 'active') {
    return 'success';
  }
  if (status === 'pending') {
    return 'warning';
  }
  if (status === 'completed') {
    return 'info';
  }

  return 'default';
}

function getContextLabel(job: Job) {
  if (job.is_internal) {
    return 'Internal';
  }

  const parts = [
    job.company_contact?.name,
    job.person_contact?.full_name ||
      [job.person_contact?.first_name, job.person_contact?.last_name]
        .filter(Boolean)
        .join(' ')
        .trim(),
  ].filter(Boolean);

  if (!parts.length) {
    return 'Client';
  }

  return `Client • ${parts.join(' • ')}`;
}

function getTotalHours(job: Job) {
  if (typeof job.total_hours === 'number') {
    return `${job.total_hours}hrs`;
  }

  return '0hrs';
}

function getTotalHoursLabel(job: Job) {
  if (!job.job_kind) return 'Total Hours';

  return job.job_kind === 'retainer' ? 'Monthly Hours' : 'Total Hours';
}

function getJobActions({
  job,
  onOpenSettings,
  onTransition,
  onRequestComplete,
  onRequestArchive,
  onRequestDelete,
  onDuplicate,
  isTransitioning,
  isCompleting,
  isDeleting,
  isDuplicating,
  canTransitionJobs,
  canDeleteJobs,
  canDuplicateJobs,
}: {
  job: Job;
  onOpenSettings: (jobId: string) => void;
  onTransition: (jobId: string, to: JobStatus) => Promise<void> | void;
  onRequestComplete: () => void;
  onRequestArchive: () => void;
  onRequestDelete: () => void;
  onDuplicate: () => void;
  isTransitioning: boolean;
  isCompleting: boolean;
  isDeleting: boolean;
  isDuplicating: boolean;
  canTransitionJobs: boolean;
  canDeleteJobs: boolean;
  canDuplicateJobs: boolean;
}) {
  const isArchived = job.status === 'archived' || job.status === 'archive';
  const isCompleted = job.status === 'completed';
  const isActive = job.status === 'active';
  const isPending = job.status === 'pending';

  const actions: Array<{
    key: string;
    label: string;
    tone?: 'danger';
    loading?: boolean;
    onSelect: () => void;
  }> = [
    {
      key: 'settings',
      label: 'Settings',
      onSelect: () => onOpenSettings(job.id),
    },
  ];

  if (canTransitionJobs && isActive && !isCompleted && !isArchived) {
    actions.push({
      key: 'complete',
      label: 'Complete Job',
      loading: isCompleting,
      onSelect: onRequestComplete,
    });
  }

  if (canDeleteJobs && isPending) {
    actions.push({
      key: 'delete',
      label: 'Delete Job',
      tone: 'danger',
      loading: isDeleting,
      onSelect: onRequestDelete,
    });
  }

  if (canDuplicateJobs) {
    actions.push({
      key: 'duplicate',
      label: 'Duplicate Job',
      loading: isDuplicating,
      onSelect: onDuplicate,
    });
  }

  if (canTransitionJobs) {
    if (isArchived) {
      actions.push({
        key: 'reactivate',
        label: 'Reactivate Job',
        loading: isTransitioning,
        onSelect: () => void onTransition(job.id, 'active'),
      });
    } else {
      actions.push({
        key: 'archive',
        label: 'Archive Job',
        loading: isTransitioning,
        onSelect: onRequestArchive,
      });
    }
  }

  return actions;
}

export function JobCard({
  job,
  onOpen,
  onOpenSettings,
  onTransition,
  onComplete,
  onDelete,
  onDuplicated,
  isTransitioning,
  isCompleting,
  isDeleting,
  canTransitionJobs,
  canDeleteJobs,
  canDuplicateJobs,
}: JobCardProps) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [duplicateJob, { isLoading: isDuplicating }] =
    useDuplicateJobMutation();

  const handleConfirmDelete = async () => {
    if (isDeleting) {
      return;
    }

    await onDelete(job.id);
    setIsDeleteModalOpen(false);
  };

  const handleConfirmArchive = async () => {
    if (isTransitioning) {
      return;
    }

    await onTransition(job.id, 'archived');
    setIsArchiveModalOpen(false);
  };

  const handleConfirmComplete = async (completionDate: Dayjs) => {
    if (isCompleting) {
      return;
    }

    await onComplete(job.id, completionDate.format('YYYY-MM-DD'));
    setIsCompleteModalOpen(false);
  };

  const handleDuplicate = async () => {
    if (isDuplicating) {
      return;
    }

    try {
      await duplicateJob({ job_id: job.id }).unwrap();
      notify({ type: 'success', message: 'Job duplicated.' });
      await onDuplicated?.();
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className={styles.jobCard}
        onClick={() => onOpen(job.id)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen(job.id);
          }
        }}
        aria-label={`Open job ${job.name}`}
      >
        <div>
          <div className={styles.jobCard__code}>
            {formatJobCode(job.job_number)}
          </div>
          <h2 className={styles.jobCard__name}>{job.name}</h2>
          <div className={styles.jobCard__meta}>
            <Badge
              label={formatJobKindLabel(job.job_kind)}
              tone="default"
              size="lg"
            />
            <Badge
              label={formatJobStatusLabel(job.status)}
              tone={getStatusBadgeTone(job.status)}
              size="lg"
            />
            <span className={styles.jobCard__context}>
              {getContextLabel(job)}
            </span>
          </div>
        </div>

        <div className={styles.jobCard__right}>
          <div>
            <div className={styles.jobCard__hoursLabel}>
              {getTotalHoursLabel(job)}
            </div>
            <p className={styles.jobCard__hoursValue}>{getTotalHours(job)}</p>
          </div>

          <div
            className={styles.jobCard__menu}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <OptionsMenu
              ariaLabel="Job actions"
              actions={getJobActions({
                job,
                onOpenSettings,
                onTransition,
                onRequestComplete: () => setIsCompleteModalOpen(true),
                onRequestArchive: () => setIsArchiveModalOpen(true),
                onRequestDelete: () => setIsDeleteModalOpen(true),
                onDuplicate: () => {
                  void handleDuplicate();
                },
                isTransitioning,
                isCompleting,
                isDeleting,
                isDuplicating,
                canTransitionJobs,
                canDeleteJobs,
                canDuplicateJobs,
              })}
            />
          </div>
        </div>
      </div>

      <ArchiveJobConfirmModal
        open={isArchiveModalOpen}
        isProcessing={isTransitioning}
        onConfirm={handleConfirmArchive}
        onClose={() => {
          if (isTransitioning) {
            return;
          }
          setIsArchiveModalOpen(false);
        }}
      />

      <CompleteJobModal
        open={isCompleteModalOpen}
        isProcessing={isCompleting}
        onConfirm={handleConfirmComplete}
        onClose={() => {
          if (isCompleting) {
            return;
          }
          setIsCompleteModalOpen(false);
        }}
      />

      <ConfirmActionModal
        open={isDeleteModalOpen}
        title="Delete job?"
        description="This action cannot be undone."
        confirmButtonLabel="Delete job"
        confirmButtonVariant="error"
        isProcessing={isDeleting}
        onConfirm={handleConfirmDelete}
        onClose={() => {
          if (isDeleting) {
            return;
          }
          setIsDeleteModalOpen(false);
        }}
      />
    </>
  );
}
